import { Show, createMemo } from 'solid-js';
import clsx from 'clsx';
import { Section } from '../../grid-system/section/section';
import { Heading } from '../../ui-components/heading/heading';
import { Button } from '../../ui-components/button/button';
import { ErrorCatcher } from '../../tools/error-catcher';
import { HeroCviCarousel } from './carousel';
import { HeroCviSingleImage } from './single-image';
import { HeroCviBlockAttributes, HeroCviImage, HeroCviProps, HeroCviSlideData } from './hero-cvi-types';

/**
 * CVI hero block. Two render modes share the same outer frame (white bg + decorative emerald
 * gradient blobs + 5/4 + spacer + 7 grid layout) and the text column; the right slot delegates
 * to `<HeroCviSingleImage>` or `<HeroCviCarousel>` depending on `props.mode`.
 */
export const HeroCvi = (props: HeroCviProps) => {
    const isSingle = createMemo(() => props.mode === 'single');

    return (
        <ErrorCatcher componentName="HeroCvi">
            {/* Outer wrapper carries the full-viewport white bg + overflow-clip so the decorative
                gradients can extend past the page-grid content area but stay within the hero section. */}
            <div class="relative w-full overflow-clip bg-cvi-bg-block-default py-cvi-section-spacious tablet-original:py-cvi-section-default">
                {/* Decorative emerald gradient blobs — absolute to the full-width wrapper. */}
                <div class="absolute inset-0 pointer-events-none" aria-hidden="true">
                    <Show
                        when={isSingle()}
                        fallback={
                            <>
                                <div
                                    class={clsx(
                                        'absolute opacity-20 bg-cvi-emerald-gradient',
                                        'size-cvi-hero-blob-mobile -top-cvi-hero-blob-back-top left-[calc(50%+var(--spacing-cvi-hero-blob-back-x))] -translate-x-1/2',
                                        'tablet-original:size-cvi-hero-blob-back-md tablet-original:top-auto tablet-original:-bottom-cvi-hero-blob-back-bot-md tablet-original:left-[20%]',
                                        'desktop:size-cvi-hero-blob-back-lg desktop:-bottom-cvi-hero-blob-back-bot-lg desktop:-left-cvi-hero-blob-back-x-lg desktop:translate-x-0'
                                    )}
                                />
                                <div
                                    class={clsx(
                                        'absolute opacity-40 bg-cvi-emerald-gradient',
                                        'size-cvi-hero-blob-mobile top-cvi-hero-blob-front-top left-[calc(50%-var(--spacing-cvi-hero-blob-front-x))] -translate-x-1/2',
                                        'tablet-original:size-cvi-hero-blob-front-md tablet-original:top-auto tablet-original:-bottom-cvi-hero-blob-front-bot-md tablet-original:left-auto tablet-original:right-[10%]',
                                        'desktop:size-cvi-hero-blob-front-lg desktop:-bottom-cvi-hero-blob-front-bot-lg desktop:left-auto desktop:-right-cvi-hero-blob-front-x-lg desktop:translate-x-0 desktop:opacity-50'
                                    )}
                                />
                            </>
                        }
                    >
                        <div
                            class={clsx(
                                'absolute bg-cvi-brand-gradient',
                                'size-cvi-hero-blob-mobile top-cvi-hero-blob-front-top left-[calc(50%-var(--spacing-cvi-hero-blob-front-x))] -translate-x-1/2',
                                'desktop:size-cvi-hero-blob-single-lg desktop:-bottom-cvi-hero-blob-single-bot-lg desktop:top-auto desktop:left-auto desktop:-right-cvi-hero-blob-single-x-lg desktop:translate-x-0'
                            )}
                        />
                    </Show>
                </div>

                <Section
                    templateShorthand={[12]}
                    widthType="bgFull"
                    removeSpacingBlock={true}
                    customCss="padding-top: 0 !important; padding-bottom: 0 !important; background: transparent;"
                >
                    <div
                        class={clsx(
                            'grid grid-cols-1 tablet-original:grid-cols-12',
                            'gap-y-cvi-7 tablet-original:gap-y-0',
                            // Own 12-col grid, so it needs the same gutter to stay aligned.
                            'tablet-original:gap-x-[var(--block-grid-gap)]'
                        )}
                    >
                        {/* Text part — full width on mobile, 5 cols from tablet-original, 4 cols from desktop. */}
                        <div class="tablet-original:col-span-5 desktop:col-span-4 flex flex-col justify-end gap-cvi-4 py-cvi-6 text-cvi-fg-default">
                            <Show when={props.largerHeading}>
                                <Heading tag="h1" variant="heading-6xl" noBlockSpacing>
                                    {props.largerHeading}
                                </Heading>
                            </Show>
                            <Show when={props.description}>
                                <p class="body-lg font-cvi-regular text-cvi-fg-default m-0">{props.description}</p>
                            </Show>
                            <Show when={isSingle() && props.cta?.url}>
                                <div class="mt-cvi-2">
                                    <Button
                                        label={props.cta!.label || ''}
                                        url={props.cta!.url}
                                        variant={
                                            (props.cta!.variant as 'arrow' | 'primary' | 'secondary' | 'secondary-neutral' | 'tertiary' | 'back' | undefined) ||
                                            'primary'
                                        }
                                        isLink={true}
                                    />
                                </div>
                            </Show>
                        </div>

                        {/* Spacer — desktop only, 1 col so text(4) + spacer(1) + images(7) = 12. */}
                        <div aria-hidden="true" class="hidden desktop:block desktop:col-span-1" />

                        {/* Images part — full width on mobile, 7 cols from tablet-original. */}
                        <div class="tablet-original:col-span-7">
                            <Show when={isSingle()} fallback={<HeroCviCarousel slides={props.slides} />}>
                                <HeroCviSingleImage
                                    image={props.image}
                                    captionName={props.captionName}
                                    captionRole={props.captionRole}
                                    captionText={props.captionText}
                                />
                            </Show>
                        </div>
                    </div>
                </Section>
            </div>
        </ErrorCatcher>
    );
};

HeroCvi.parseProps = (atts: HeroCviBlockAttributes): HeroCviProps => {
    const toImage = (raw: Partial<HeroCviImage> | undefined): HeroCviImage | undefined => {
        if (!raw?.url) return undefined;
        return {
            url: raw.url,
            width: raw.width,
            height: raw.height,
            sign: raw.sign,
            focus: raw.focus,
            altText: raw.altText,
        };
    };

    const slides: HeroCviSlideData[] = (atts.slides ?? []).map((slide) => ({
        largerImage: toImage(slide.largerImage),
        smallerImage: toImage(slide.smallerImage),
        captionName: slide.captionName,
        captionRole: slide.captionRole,
    }));

    return {
        mode: atts.mode ?? 'carousel',
        largerHeading: atts.largerHeading,
        description: atts.description,
        slides,
        image: toImage(atts.image),
        useFocusPoint: atts.useFocusPoint,
        cta: atts.cta,
        captionName: atts.captionName,
        captionRole: atts.captionRole,
        captionText: atts.captionText,
    };
};
